import React, { useEffect, useState, useContext } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import { AuthContext } from "../../context/authcontext";
import { API_BASE_URL } from "../../environment";
import { storeCurrencyCode } from "../../components/currencyFormat";
import Pattern from "../../components/ui/Pattern";
import { isMobileOrTabletDevice } from "../../utils/deviceDetection";

const MySwal = withReactContent(Swal);

const Login = () => {
  const route = all_routes;
  const navigate = useNavigate();
  const location = useLocation();
  const {
    auth,
    setAuth,
    setSelectedTenantId,
    setLifecycleTypeNames,
    setIsTeamManager,
  } = useContext(AuthContext);
  const [isLoading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [showPattern] = useState(() => !isMobileOrTabletDevice());

  const togglePasswordVisibility = () => {
    setPasswordVisible((prev) => !prev);
  };

  useEffect(() => {
    if (!auth) {
      localStorage.setItem("menuOpened", "Dashboard");
    }

    const params = new URLSearchParams(location.search);
    const isSignupSuccess = params.get("signup") === "success";

    if (isSignupSuccess) {
      MySwal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Signup successful! Please log in.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      }).then(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete("signup");
        window.history.replaceState({}, document.title, url.pathname);
      });
    }
  }, [auth, location.search]);

  const handleResendVerification = async (userEmail: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/user/resend-verification/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: userEmail }),
      });

      if (!res.ok) throw new Error("Failed to resend verification email");

      const data = await res.json();

      MySwal.fire({
        icon: "success",
        title: "Verification Email Sent",
        text:
          data.message ||
          "Verification email has been sent successfully. Please check your inbox.",
        showConfirmButton: true,
      });
    } catch (err) {
      console.error(err);
      MySwal.fire({
        icon: "error",
        title: "Failed to Send Verification Email",
        text: "Could not send verification email. Please try again.",
        showConfirmButton: true,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/login/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });

      const data = await res.json();
      if (!res.ok) {
        if (data?.message?.toLowerCase().includes("account not verified")) {
          MySwal.fire({
            icon: "error",
            title: "Account Not Verified",
            text: "Please verify your account first",
            showCancelButton: true,
            confirmButtonText: "Resend Verification Link",
            cancelButtonText: "Close",
            confirmButtonColor: "#824eeb",
          }).then((result) => {
            if (result.isConfirmed) {
              handleResendVerification(email.trim());
            }
          });
          return;
        }
        if (
          data?.message?.toLowerCase().includes("deactivated") ||
          data?.message?.toLowerCase().includes("not active")
        ) {
          MySwal.fire({
            icon: "error",
            title: "Account Deactivated",
            text: "You are deactivated. Please contact support.",
          });
          return;
        }
        MySwal.fire({
          icon: "error",
          title: "Invalid Credentials",
          text: "The email or password you entered is incorrect.",
        });
        return;
      }

      const tenantId = data?.tenants?.[0]?.tenant_id || null;
      const teamManager = data?.tenants?.[0]?.team_manager || false;
      const creds = btoa(`${email.trim()}:${password}`);
      localStorage.setItem("authCred", creds);
      localStorage.setItem("isTeamManager", teamManager.toString());
      setAuth(data);

      const activeTenant =
        data?.tenants?.find((t: any) => String(t.tenant_id) === String(tenantId)) ||
        data?.tenants?.[0];
      const initialCurrency =
        activeTenant?.lifecycle_types?.find((lt: any) => lt.currency_code)?.currency_code ||
        activeTenant?.lifecycle_types?.[0]?.currency_code;
      if (initialCurrency) {
        storeCurrencyCode(initialCurrency);
      }

      const isSuperAdmin =
        data?.tenants?.some(
          (t: any) =>
            t.role_key?.toLowerCase() === "superadmin" ||
            t.role_key?.toLowerCase() === "super_admin" ||
            t.role_name?.toLowerCase() === "superadmin" ||
            t.role_name?.toLowerCase() === "super_admin" ||
            t.role_key?.toLowerCase() === "platform_analyst" ||
            t.role_name?.toLowerCase() === "platformanalyst" ||
            t.role_key?.toLowerCase() === "apiadmin" ||
            t.role_key?.toLowerCase() === "api_admin" ||
            t.role_name?.toLowerCase() === "apiadmin" ||
            t.role_name?.toLowerCase() === "api_admin"
        ) || false;
      if (!isSuperAdmin) {
        setSelectedTenantId(tenantId ? String(tenantId) : null);
      }

      const tenantLifecycleTypes: any[] = Array.isArray(activeTenant?.lifecycle_types)
        ? activeTenant.lifecycle_types
        : [];
      const lifecycleNames = tenantLifecycleTypes.reduce(
        (map: Record<string, string>, item: any) =>
          item?.lifecycle_type_key && item?.lifecycle_type_display_name
            ? { ...map, [item.lifecycle_type_key]: item.lifecycle_type_display_name }
            : map,
        {}
      );
      if (Object.keys(lifecycleNames).length) {
        localStorage.setItem("lifecycleTypeNames", JSON.stringify(lifecycleNames));
        setLifecycleTypeNames(lifecycleNames);
      }

      setIsTeamManager(teamManager);
      MySwal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: `Welcome ${data.first_name} ${data.last_name}`,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Login failed";
      MySwal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: msg,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="account-content">
      <div className="login-wrapper w-100 min-vh-100 position-relative overflow-hidden">
        {showPattern && (
          <div className="position-absolute w-100 h-100 top-0 start-0 pointer-events-none" style={{ zIndex: 0 }}><Pattern /></div>
        )}
        <div className="login-content w-100 position-relative" style={{ zIndex: 1 }}>
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-6 col-lg-4">
                <form className="login-user-info" onSubmit={handleSubmit}>
                  <div className="text-center mb-4">
                    <ImageWithBasePath
                      src="assets/img/Logo.png"
                      className="img-fluid login-logo"
                      alt="Logo"
                    />
                  </div>

                  <div className="mb-4">
                    <h4>Sign In</h4>
                    <p>Access the CRMS panel using your email and passcode.</p>
                  </div>

                  <div className="mb-3">
                    <label className="col-form-label">Email Address</label>
                    <div className="position-relative">
                      <span className="input-icon-addon">
                        <i className="ti ti-mail" />
                      </span>
                      <input
                        type="email"
                        className="form-control"
                        value={email ?? ""}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="col-form-label">Password</label>
                    <div className="pass-group">
                      <input
                        type={isPasswordVisible ? "text" : "password"}
                        className="pass-input form-control"
                        value={password ?? ""}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      <span
                        className={`ti toggle-password ${isPasswordVisible ? "ti-eye" : "ti-eye-off"
                          }`}
                        onClick={togglePasswordVisibility}
                      />
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="form-check form-check-md">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="checkebox-md"
                        defaultChecked
                      />
                      <label
                        className="form-check-label"
                        htmlFor="checkebox-md"
                      >
                        Remember Me
                      </label>
                    </div>
                    <div>
                      <Link
                        to={route.forgotPassword}
                        className="text-primary fw-medium link-hover"
                      >
                        Forgot Password?
                      </Link>
                    </div>
                  </div>

                  <div className="mb-3">
                    <button
                      type="submit"
                      className="btn btn-primary w-100"
                      disabled={isLoading}
                    >
                      <i
                        className="bi bi-box-arrow-in-right me-2"
                        style={{ fontSize: "1rem" }}
                      />
                      {isLoading ? "Signing in..." : "Sign In"}
                    </button>
                  </div>

                  <div className="mb-3 text-center">
                    <h6>
                      New on our platform? {"  "} &nbsp;
                      <Link
                        to={route.register}
                        className="text-purple link-hover"
                      >
                        Create an account
                      </Link>
                    </h6>
                  </div>
                </form>
                <p className="text-muted text-center mt-3">
                  By signing in, you agree to our{" "}
                  <Link
                    to={"https://gocloudee.com/privacy-policy"}
                    className="text-purple link-hover"
                    target="_blank"
                  >
                    Privacy Policy
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
